import type {
  ComponentType,
  HTMLAttributes,
  KeyboardEvent,
  MouseEvent,
  ReactNode,
  Ref,
  RefObject,
} from 'react';

// App-owned contracts. Keep persisted sort/filter/column models stable across table engines.
export type GridValidRowModel = Record<string, any>;
export type GridRowId = string | number;
export type GridSortModel = { field: string; sort: 'asc' | 'desc' | null | undefined }[];
export type GridColumnVisibilityModel = Record<string, boolean>;
export enum GridLogicOperator {
  And = 'and',
  Or = 'or',
}
export interface GridFilterItem {
  id?: GridRowId;
  field: string;
  operator: string;
  value?: any;
  unitMultiplier?: number;
}
export interface GridFilterModel {
  items: GridFilterItem[];
  logicOperator?: GridLogicOperator | 'and' | 'or';
}
export interface GridFilterInputValueProps {
  item: GridFilterItem;
  applyValue: (item: GridFilterItem) => void;
  focusElementRef?: Ref<{ focus: () => void }>;
}
export interface GridFilterOperator {
  value: string;
  label: string;
  requiresFilterValue?: boolean;
  InputComponent?: ComponentType<GridFilterInputValueProps>;
  getApplyFilterFn: (item: GridFilterItem) => ((value: any) => boolean) | null;
}
export interface GridRowParams<R = any> {
  id: GridRowId;
  row: R;
}
export type GridRowClassNameParams<R = any> = GridRowParams<R>;
export interface GridRowHeightParams {
  id: GridRowId;
  model: GridValidRowModel;
}
export interface GridCellParams<R = any> extends GridRowParams<R> {
  field: string;
  value: any;
  api: DataTableApi;
}
export interface GridRenderCellParams<R = any> extends GridCellParams<R> {
  selectionControl?: ReactNode;
}
export interface GridColDef<R extends GridValidRowModel = any> {
  field: string;
  headerName?: string;
  width?: number;
  minWidth?: number;
  maxWidth?: number;
  flex?: number;
  type?: 'string' | 'number' | 'date' | 'dateTime' | 'boolean' | 'singleSelect';
  align?: 'left' | 'center' | 'right';
  headerAlign?: 'left' | 'center' | 'right';
  sortable?: boolean;
  filterable?: boolean;
  hideable?: boolean;
  resizable?: boolean;
  disableColumnMenu?: boolean;
  disableReorder?: boolean;
  /** Stays at the table's right edge while the columns scroll sideways under it. */
  pinned?: 'right';
  valueOptions?: (string | number | { value: any; label: string })[];
  valueGetter?: (value: any, row: R) => any;
  valueFormatter?: (value: any) => ReactNode;
  renderCell?: (params: GridRenderCellParams<R>) => ReactNode;
  filterOperators?: GridFilterOperator[];
  sortComparator?: (
    a: any,
    b: any,
    aParams: { api: Pick<DataTableApi, 'getSortModel'> },
    bParams: { api: Pick<DataTableApi, 'getSortModel'> },
  ) => number;
  colSpan?: (
    value: any,
    row: R,
    column: GridColDef<R>,
    api: RefObject<DataTableApi>,
  ) => number | undefined;
}
export interface GridRowSelectionModel {
  type: 'include' | 'exclude';
  ids: Set<GridRowId>;
}
export type TableEvent =
  | 'stateChange'
  | 'filterModelChange'
  | 'sortModelChange'
  | 'rowSelectionChange';
export interface DataTableApi {
  getAllColumns: () => GridColDef[];
  getVisibleColumns: () => GridColDef[];
  getFilteredSortedRowIds: () => GridRowId[];
  getRowsCount: () => number;
  getSelectedRows: () => Map<GridRowId, GridValidRowModel>;
  getSortModel: () => GridSortModel;
  setSortModel: (model: GridSortModel) => void;
  setFilterModel: (model: GridFilterModel) => void;
  setRowSelectionModel: (model: GridRowSelectionModel | GridRowId[]) => void;
  subscribeEvent: (event: TableEvent, callback: () => void) => () => void;
  scrollToIndexes: (position: { rowIndex: number; colIndex?: number }) => void;
  scrollToRow: (id: GridRowId) => boolean;
  /** Focuses the name search (with `searchField`) and selects what's in it. */
  focusSearch: () => void;
}
/** Extra attributes for a row element. The table keeps its own role, ARIA, class, style and click. */
export type DataTableRowProps = Omit<
  HTMLAttributes<HTMLDivElement>,
  'children' | 'className' | 'style' | 'role' | 'onClick'
>;
export interface DataTableProps<R extends GridValidRowModel> {
  rows: readonly R[];
  columns: readonly GridColDef<R>[];
  apiRef?: RefObject<DataTableApi | null>;
  className?: string;
  'aria-label'?: string;
  rowHeight?: number;
  columnHeaderHeight?: number;
  getRowId?: (row: R) => GridRowId;
  getRowHeight?: (params: GridRowHeightParams) => number | null;
  getRowSpacing?: (params: { isFirstVisible: boolean; isLastVisible: boolean }) => {
    top: number;
    bottom: number;
  };
  getRowClassName?: (params: GridRowParams<R>) => string;
  onRowClick?: (params: GridRowParams<R>, event: MouseEvent) => void;
  onCellKeyDown?: (params: GridCellParams<R>, event: KeyboardEvent) => void;
  checkboxSelection?: boolean;
  isRowSelectable?: (params: GridRowParams<R>) => boolean;
  disableRowSelectionOnClick?: boolean;
  rowSelectionModel?: GridRowSelectionModel;
  onRowSelectionModelChange?: (model: GridRowSelectionModel) => void;
  sortModel?: GridSortModel;
  onSortModelChange?: (model: GridSortModel) => void;
  sortingMode?: 'client' | 'server';
  sortingOrder?: ('asc' | 'desc' | null)[];
  filterModel?: GridFilterModel;
  onFilterModelChange?: (model: GridFilterModel) => void;
  disableColumnFilter?: boolean;
  columnVisibilityModel?: GridColumnVisibilityModel;
  onColumnVisibilityModelChange?: (model: GridColumnVisibilityModel) => void;
  onColumnOrderChange?: () => void;
  loading?: boolean;
  /** Optional name search, combined with column filters using AND. */
  searchField?: string;
  /**
   * With `onSearchChange`, the search is the caller's: the field starts from `searchValue`, reports
   * every keystroke, and the caller filters `rows` itself, so the table applies no name filter.
   */
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  /** The search field's placeholder and accessible name. */
  searchPlaceholder?: string;
  /** Shown when the search and filters leave no rows. */
  searchEmptyText?: string;
  /** Beside the search field, about the rows on screen (section rows left out). Default: a count. */
  renderSearchSummary?: (rows: R[]) => ReactNode;
  /**
   * Rows that head a group of the rows after them, such as "Pinned". The search and filters skip
   * them, and one shows only while a row of its group does.
   */
  isSectionRow?: (row: R) => boolean;
  initialState?: {
    columns?: { columnVisibilityModel?: GridColumnVisibilityModel };
    sorting?: { sortModel?: GridSortModel };
  };
  slots?: {
    noRowsOverlay?: ComponentType;
    noResultsOverlay?: ComponentType;
    /** Replaces the progress bar while `loading` and no rows are on screen yet. */
    loadingOverlay?: ComponentType;
    /** Rendered before the name search, on its line (needs `searchField`). */
    toolbarStart?: ComponentType;
    /** Rendered after the search summary, on the search's line. */
    toolbarEnd?: ComponentType;
  };
  slotProps?: {
    /** Spread onto every row element, which carries `data-id` (e.g. `draggable` and `onDragStart`). */
    row?: DataTableRowProps;
  };
  getTogglableColumns?: (columns: GridColDef[]) => string[];
}
